import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import useGeneralState from 'utils/hooks/useGeneralState';

interface MobileNavigationContextProps {
  showSection: number;
  setShowSection: (
    newState: number,
    operation?: 'Update' | 'Push' | 'Pop' | 'Delete',
    customOnBack?: ((currentState: number) => number) | undefined,
  ) => void;
}

const MobileNavigationContext = createContext<
  MobileNavigationContextProps | undefined
>(undefined);

export const MobileNavigationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { state: showSection, setState: setShowSection } = useGeneralState({
    id: 'MobileNavigation',
    initialState: 0,
  });

  // Memoised so a navigation-stack change elsewhere in the app does not hand
  // every consumer a new context object (and re-run their effects) when the
  // section itself has not moved.
  const value = useMemo(
    () => ({ showSection, setShowSection }),
    [showSection, setShowSection],
  );

  return (
    <MobileNavigationContext.Provider value={value}>
      {children}
    </MobileNavigationContext.Provider>
  );
};

export const useMobileNavigation = (): MobileNavigationContextProps => {
  const context = useContext(MobileNavigationContext);
  if (!context) {
    throw new Error(
      'useMobileNavigation must be used within a MobileNavigationProvider',
    );
  }
  return context;
};
